import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Map, Hammer, TrendingUp } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const FRAMEWORK = [
  { icon: Search, title: "The Challenge", text: "What problem was the client facing? Every case study starts with the real situation on the ground." },
  { icon: Map, title: "The Strategy", text: "The approach we chose — and just as importantly, why we chose it over the alternatives." },
  { icon: Hammer, title: "The Execution", text: "The actual work: campaigns, content, builds and everything shipped along the way." },
  { icon: TrendingUp, title: "The Outcome", text: "Genuine, verified results only. If we can't verify a number, we don't publish it." },
];

const CaseStudies = () => {
  useEffect(() => {
    document.title = "Case Studies — Click World Online";
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="Case Studies"
        title={<>Proof, <span className="text-electric">properly told.</span></>}
        text="Real client stories follow a real structure — challenge, strategy, execution, outcome. No invented numbers, ever."
      />

      <section className="bg-navy-deep" data-testid="case-framework-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {FRAMEWORK.map((f, i) => (
              <Reveal key={f.title} delay={0.06 * i}>
                <div data-testid={`framework-${f.title.toLowerCase().replace(/\s/g, "-")}`} className="relative bg-navy-light border border-white/10 rounded-3xl p-8 h-full overflow-hidden hover:border-electric/40 transition-colors duration-300">
                  <span className="font-display font-black text-5xl text-outline absolute top-5 right-6" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <f.icon size={26} className="text-electric" />
                  <h2 className="mt-6 font-display text-xl font-extrabold text-mist tracking-tight">{f.title}</h2>
                  <p className="mt-3 text-slatec text-sm leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20">
            <div className="relative bg-navy grain border border-white/10 rounded-3xl p-10 lg:p-16 overflow-hidden text-center">
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-64 bg-brand/20 blur-[110px] rounded-full" aria-hidden="true" />
              <div className="relative">
                <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tighter text-mist">
                  Full case studies are being documented.
                </h2>
                <p className="mt-6 text-slatec max-w-2xl mx-auto leading-relaxed">
                  We're preparing detailed case studies with verified data for our client work. Until they're ready, we
                  won't publish a single invented metric — instead, explore the brands we work with, or ask us directly
                  and we'll walk you through real engagements.
                </p>
                <div className="mt-10 flex flex-wrap justify-center gap-4">
                  <Link to="/portfolio" data-testid="case-view-work" className="inline-flex items-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300">
                    View Our Work <ArrowUpRight size={18} />
                  </Link>
                  <Link to="/contact" data-testid="case-ask-us" className="inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric font-semibold px-8 py-4 rounded-full transition-colors duration-300">
                    Ask Us Directly
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
};

export default CaseStudies;
